import React, { useEffect, useMemo, useState } from 'react';
import { SalaryDateFilters } from '../salary-generator/filters/salary-date-filters';
import { SalaryLecturerList } from '../salary-generator/filters/salary-lecturer-list';
import { ProfessorsSalariesStatusesType } from '../../../api/types/accountants/salaries-statuses/professors-salaries-statuses.type';
import { retrieveEmployeeSalaryPaymentStatusOperation } from '../salary-generator/operations/retrieve-employee-salary-payment-status-operation';
import { generatePayrollReportFlatOperation } from './operations/generate-payroll-report-flat.operation';
import { exportPayrollReportFlatToExcelOperation } from './operations/export-payroll-report-flat-to-excel.operation';
import { PendingWorkingHourFlat } from '../../../api/types/accountants/salaries-pending-payments-flat/pending-working-hour-flat.type';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { WarningDialog } from '../../../component/ui/warning-dialog/warning-dialog';
import { paySalaryForProfessorFlatOperation } from './operations/pay-salary-for-professor-flat.operation';
import { TableColumnLongTextWithInformation } from '../../../component/table-componenets/cusom-column/table-column-long-text-with-information';

export const SalaryGeneratorFlat = () => {
  const getDefaultDateRange = (): [Date, Date] => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [startDate, endDate];
  };

  const [pendingWorkingHours, setPendingWorkingHours] = useState<
    PendingWorkingHourFlat[]
  >([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [dateSearchLoading, setDateSearchLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [professorStatuses, setProfessorStatuses] =
    useState<ProfessorsSalariesStatusesType>();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    getDefaultDateRange(),
  );
  const [
    includeHistoricalMissingPayments,
    setIncludeHistoricalMissingPayments,
  ] = useState(false);
  const [branchIds, setBranchIds] = useState<number[]>(() => {
    const firstId = (globalSettingsBranches.branches ?? []).find(
      (b) => Number.isInteger(b.id) && b.id >= 1,
    )?.id;
    return firstId != null ? [firstId] : [];
  });
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [approveDialogVisible, setApproveDialogVisible] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination state (same pattern as CoursesListPage)
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  /** Same fallback as export: when parent `branchIds` is still empty (e.g. branches loaded late), use first settings branch. */
  const effectiveBranchIds = useMemo(() => {
    if (branchIds.length > 0) {
      return branchIds;
    }
    const firstId = (globalSettingsBranches.branches ?? []).find(
      (b) => Number.isInteger(b.id) && b.id >= 1,
    )?.id;
    return firstId != null ? [firstId] : [];
  }, [branchIds, globalSettingsBranches.branches]);

  const paymentBranchId = effectiveBranchIds[0] ?? null;

  const fetchEmployeePaymentStatus = async (
    startDate: Date,
    endDate: Date,
    shouldIncludeRetroactive: boolean,
    branchIds: number[],
  ) => {
    await retrieveEmployeeSalaryPaymentStatusOperation(
      startDate,
      endDate,
      shouldIncludeRetroactive,
      branchIds,
      setUsersLoading,
      setProfessorStatuses,
    );
  };

  useEffect(() => {
    const defaultDates = getDefaultDateRange();
    const firstId = (globalSettingsBranches.branches ?? []).find(
      (b) => Number.isInteger(b.id) && b.id >= 1,
    )?.id;
    const defaultBranchIds = firstId != null ? [firstId] : [];
    setBranchIds(defaultBranchIds);
    fetchEmployeePaymentStatus(
      defaultDates[0],
      defaultDates[1],
      false,
      defaultBranchIds,
    );
  }, []);

  const handleDateSearch = async (filters: {
    dateRange: [Date | null, Date | null];
    includeHistoricalMissingPayments: boolean;
    branchIds: number[];
  }) => {
    try {
      setDateSearchLoading(true);
      setDateRange(filters.dateRange);
      setIncludeHistoricalMissingPayments(
        filters.includeHistoricalMissingPayments,
      );
      setBranchIds(filters.branchIds);

      await fetchEmployeePaymentStatus(
        filters.dateRange[0]!,
        filters.dateRange[1]!,
        filters.includeHistoricalMissingPayments,
        filters.branchIds,
      );

      if (
        selectedUserIds.length > 0 &&
        filters.dateRange[0] &&
        filters.dateRange[1]
      ) {
        setOffset(0);
        await generatePayrollReportFlatOperation(
          selectedUserIds,
          filters.branchIds,
          filters.dateRange[0],
          filters.dateRange[1],
          filters.includeHistoricalMissingPayments,
          setTableLoading,
          setPendingWorkingHours,
          setTotal,
          0,
          limit,
        );
      }
    } finally {
      setDateSearchLoading(false);
    }
  };

  const onUserSelectionChange = async (userIds: number[]) => {
    setSelectedUserIds(userIds);
    setOffset(0);

    if (userIds.length === 0) {
      setPendingWorkingHours([]);
      setTotal(0);
      return;
    }

    const defaultDates = getDefaultDateRange();
    const startDate = dateRange[0] || defaultDates[0];
    const endDate = dateRange[1] || defaultDates[1];

    await generatePayrollReportFlatOperation(
      userIds,
      effectiveBranchIds,
      startDate,
      endDate,
      includeHistoricalMissingPayments,
      setTableLoading,
      setPendingWorkingHours,
      setTotal,
      0,
      limit,
    );
  };

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);

    if (!dateRange[0] || !dateRange[1] || selectedUserIds.length === 0) {
      return;
    }

    await generatePayrollReportFlatOperation(
      selectedUserIds,
      effectiveBranchIds,
      dateRange[0],
      dateRange[1],
      includeHistoricalMissingPayments,
      setTableLoading,
      setPendingWorkingHours,
      setTotal,
      newOffset,
      newLimit,
    );
  };

  const tableRows = useMemo(() => {
    const getLocalDateKey = (date: Date) => {
      const d = new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    type Candidate = {
      workingHourId: number;
      travelRate: number;
    };

    const bestTravelOwnerByUserDateBranch = new Map<string, Candidate>();

    pendingWorkingHours.forEach((row) => {
      if (!row.addTravel) {
        return;
      }
      if (row.travelRate === null || row.travelRate === undefined) {
        return;
      }

      const travelRate = Number(row.travelRate);
      if (!Number.isFinite(travelRate) || travelRate <= 0) {
        return;
      }

      const key = `${row.userId}-${getLocalDateKey(row.startAt)}-${row.branchId}`;
      const existing = bestTravelOwnerByUserDateBranch.get(key);

      if (!existing) {
        bestTravelOwnerByUserDateBranch.set(key, {
          workingHourId: row.workingHourId,
          travelRate,
        });
        return;
      }

      // Choose a single row to "own" the travel payment:
      // - highest travelRate wins
      // - if tie, lowest workingHourId wins (stable)
      if (
        travelRate > existing.travelRate ||
        (travelRate === existing.travelRate &&
          row.workingHourId < existing.workingHourId)
      ) {
        bestTravelOwnerByUserDateBranch.set(key, {
          workingHourId: row.workingHourId,
          travelRate,
        });
      }
    });

    return pendingWorkingHours.map((row) => {
      const dateKey = getLocalDateKey(row.startAt);
      const key = `${row.userId}-${dateKey}-${row.branchId}`;
      const owner = bestTravelOwnerByUserDateBranch.get(key);

      return {
        ...row,
        _rowKey: String(row.workingHourId),
        _startAt: new Date(row.startAt),
        _endAt: new Date(row.endAt),
        _dateKey: dateKey,
        _travelToAdd:
          owner && owner.workingHourId === row.workingHourId
            ? owner.travelRate
            : 0,
      };
    });
  }, [pendingWorkingHours]);

  const formatDateTime = (date: Date) =>
    new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(date));

  const formatTime = (date: Date) =>
    new Intl.DateTimeFormat('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));

  const alignRight = { textAlign: 'right' as const };

  const displayTypeToHebrew = (displayType: string) => {
    switch (displayType) {
      case 'one_hour':
        return 'שעתי';
      case 'academic_hour':
        return 'אקדמי';
      default:
        return displayType;
    }
  };

  const formatHourUnits = (startAt: Date, endAt: Date, displayType: string) => {
    const startMs = new Date(startAt).getTime();
    const endMs = new Date(endAt).getTime();
    const diffMinutes = Math.max(0, Math.floor((endMs - startMs) / 60000)); // no seconds

    const unitMinutes = displayType === 'academic_hour' ? 45 : 60;
    const units = diffMinutes / unitMinutes;

    return new Intl.NumberFormat('he-IL', { maximumFractionDigits: 2 }).format(
      units,
    );
  };

  const calcHourUnitsNumber = (
    startAt: Date,
    endAt: Date,
    displayType: string,
  ) => {
    const startMs = new Date(startAt).getTime();
    const endMs = new Date(endAt).getTime();
    const diffMinutes = Math.max(0, Math.floor((endMs - startMs) / 60000)); // no seconds
    const unitMinutes = displayType === 'academic_hour' ? 45 : 60;
    return diffMinutes / unitMinutes;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
    }).format(value);
  };

  const handleApprovePayment = async () => {
    const ok = await paySalaryForProfessorFlatOperation(
      paymentBranchId,
      dateRange[0],
      dateRange[1],
      selectedUserIds,
      setApproveLoading,
    );

    if (ok) {
      setApproveDialogVisible(false);
      if (dateRange[0] && dateRange[1] && selectedUserIds.length > 0) {
        await generatePayrollReportFlatOperation(
          selectedUserIds,
          effectiveBranchIds,
          dateRange[0],
          dateRange[1],
          includeHistoricalMissingPayments,
          setTableLoading,
          setPendingWorkingHours,
          setTotal,
          offset,
          limit,
        );
      }
    }
  };

  const approveDialogSummary = useMemo(() => {
    const branchId = paymentBranchId;
    const branchName =
      branchId != null
        ? (globalSettingsBranches.branches?.find((b) => b.id === branchId)
            ?.branchName ?? `מזהה ${branchId}`)
        : '—';
    const period =
      dateRange[0] && dateRange[1]
        ? `${formatDate(dateRange[0])} – ${formatDate(dateRange[1])}`
        : '—';
    return `שלוחה: ${branchName}\nתקופה: ${period}\nמספר מרצים: ${selectedUserIds.length}`;
  }, [paymentBranchId, dateRange, selectedUserIds.length]);

  const tableStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    background: 'white',
    borderRadius: '8px',
    border: '1px solid var(--surface-200)',
    padding: '16px',
  };

  const exportExcelIcon = exportLoading
    ? 'pi pi-spin pi-spinner'
    : 'pi pi-file-excel';

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SalaryDateFilters
          onSearch={handleDateSearch}
          loading={dateSearchLoading}
        />
        <SalaryLecturerList
          professorStatuses={professorStatuses}
          multiple
          onSelectionChange={onUserSelectionChange}
        />
      </div>

      <div style={tableStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <div style={{ fontSize: '14px', color: '#495057' }}>
            מספר שורות: {total}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              style={{
                height: '34px',
                width: '34px',
                backgroundColor: '#10b981',
                borderColor: '#10b981',
              }}
              icon={exportExcelIcon}
              tooltip="ייצוא לאקסל"
              disabled={
                exportLoading ||
                tableLoading ||
                selectedUserIds.length === 0 ||
                !dateRange[0] ||
                !dateRange[1]
              }
              onClick={() =>
                exportPayrollReportFlatToExcelOperation(
                  selectedUserIds,
                  effectiveBranchIds,
                  dateRange[0]!,
                  dateRange[1]!,
                  includeHistoricalMissingPayments,
                  setExportLoading,
                )
              }
            />

            <Button
              style={{ height: '34px' }}
              label={
                selectedUserIds.length > 0
                  ? `אשר תשלום (${selectedUserIds.length})`
                  : 'אשר תשלום'
              }
              icon="pi pi-check"
              className="p-button-success"
              onClick={() => setApproveDialogVisible(true)}
              disabled={
                selectedUserIds.length === 0 ||
                !dateRange[0] ||
                !dateRange[1] ||
                paymentBranchId == null
              }
            />
          </div>
        </div>
        <DataTable
          value={tableRows}
          loading={tableLoading || usersLoading}
          dataKey="_rowKey"
          stripedRows
          size="small"
          paginator
          lazy={true}
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>
              {selectedUserIds.length > 0
                ? 'לא נמצאו נתונים לתקופה שנבחרה'
                : 'בחר מרצה להצגת נתונים'}
            </div>
          }
        >
          <Column
            field="branchName"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            header="שלוחה"
          />
          <Column
            header="מרצה"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) =>
              `${String(row.firstName ?? '').trim()} ${String(
                row.lastName ?? '',
              ).trim()}`.trim()
            }
          />
          <Column
            field="courseName"
            header="קורס"
            headerStyle={alignRight}
            bodyStyle={alignRight}
          />
          <Column
            field="additionalCourseName"
            header="שם קורס נוסף"
            headerStyle={alignRight}
            bodyStyle={alignRight}
          />
          <Column
            field="activityType"
            header="סוג פעילות"
            headerStyle={alignRight}
            bodyStyle={alignRight}
          />
          <Column
            field="workingHourNotes"
            header="הערות שעות עבודה"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) =>
              row.workingHourNotes && String(row.workingHourNotes).trim() ? (
                <TableColumnLongTextWithInformation
                  text={String(row.workingHourNotes)}
                  uniqueId={row._rowKey}
                  maxLength={7}
                />
              ) : (
                <span>-</span>
              )
            }
          />
          <Column
            field="displayType"
            header="סוג שעה"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => displayTypeToHebrew(String(row.displayType))}
          />
          <Column
            field="_startAt"
            header="תאריך"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => formatDate(row._startAt)}
          />
          <Column
            field="_startAt"
            header="התחלה"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => formatTime(row._startAt)}
          />
          <Column
            field="_endAt"
            header="סיום"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => formatTime(row._endAt)}
          />
          <Column
            field="breakMinutes"
            header="דקות הפסקה"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) =>
              row.breakMinutes !== null && row.breakMinutes !== undefined
                ? row.breakMinutes
                : '—'
            }
          />
          <Column
            field="hoursWorked"
            header="שעות (אקדמי/מלאות)"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) =>
              formatHourUnits(row._startAt, row._endAt, String(row.displayType))
            }
          />
          <Column
            field="rate"
            header="תעריף"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) =>
              row.rate !== null && row.rate !== undefined ? `₪${row.rate}` : ''
            }
          />
          <Column
            field="travelRate"
            header={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'right',
                  flexDirection: 'row',
                  width: '100%',
                  gap: '6px',
                }}
              >
                <Tooltip
                  target=".travel-rate-header-info-icon"
                  position="top"
                />
                <i
                  className="pi pi-info-circle travel-rate-header-info-icon"
                  data-pr-tooltip="נסיעות מחושבות פעם אחת לכל יום ולכל שלוחה"
                  style={{
                    cursor: 'pointer',
                    color: 'var(--surface-600)',
                    fontSize: '0.95rem',
                  }}
                />
                <span>תעריף נסיעות</span>
              </div>
            }
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => {
              if (row.travelRate === null || row.travelRate === undefined) {
                return '';
              }

              const travelText = `₪${row.travelRate}`;
              const isBlocked =
                row.addTravel &&
                row._travelToAdd === 0 &&
                Number(row.travelRate) > 0;

              if (!isBlocked) {
                return travelText;
              }

              const uniqueId = row._rowKey;
              const tooltipText = 'נסיעות כבר נספרו ליום זה';

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '6px',
                    width: '100%',
                  }}
                >
                  <span
                    style={{ opacity: 0.5, textDecoration: 'line-through' }}
                  >
                    {travelText}
                  </span>
                  <Tooltip
                    target={`.travel-counted-info-icon-${uniqueId}`}
                    position="top"
                  />
                  <i
                    className={`pi pi-info-circle travel-counted-info-icon-${uniqueId}`}
                    data-pr-tooltip={tooltipText}
                    style={{
                      cursor: 'pointer',
                      color: 'var(--surface-500)',
                      fontSize: '0.95rem',
                    }}
                  />
                </div>
              );
            }}
          />
          <Column
            field="addTravel"
            header="נסיעות"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => (row.addTravel ? 'כן' : 'לא')}
          />
          {/*<Column
            field="_total"
            header="סה״כ"
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => {
              const rate = Number(row.rate);
              if (!Number.isFinite(rate)) {
                return '';
              }

              const units = calcHourUnitsNumber(
                row._startAt,
                row._endAt,
                String(row.displayType),
              );

              const travelToAdd = Number(row._travelToAdd) || 0;
              const total = rate * units + travelToAdd;
              return (
                <span style={{ color: '#16a34a', fontWeight: 700 }}>
                  {formatCurrency(total)}
                </span>
              );
            }}
          />*/}
          <Column
            field="_totalWithBreak"
            header={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'right',
                  flexDirection: 'row',
                  width: '100%',
                  gap: '6px',
                }}
              >
                <Tooltip
                  target=".total-with-break-header-info-icon"
                  position="top"
                />
                <i
                  className="pi pi-info-circle total-with-break-header-info-icon"
                  data-pr-tooltip="הסכום כולל את השכר ואת ניכוי דקות ההפסקה בחישוב"
                  style={{
                    cursor: 'pointer',
                    color: 'var(--surface-600)',
                    fontSize: '0.95rem',
                  }}
                />
                <span>סה״כ</span>
              </div>
            }
            headerStyle={alignRight}
            bodyStyle={alignRight}
            body={(row: any) => {
              const rate = Number(row.rate);
              if (!Number.isFinite(rate)) {
                return '';
              }

              const units = calcHourUnitsNumber(
                row._startAt,
                row._endAt,
                String(row.displayType),
              );
              const unitMinutes =
                String(row.displayType) === 'academic_hour' ? 45 : 60;
              const breakMinutes = Number(row.breakMinutes);
              const breakUnits =
                Number.isFinite(breakMinutes) && breakMinutes > 0
                  ? breakMinutes / unitMinutes
                  : 0;
              const adjustedUnits = Math.max(0, units - breakUnits);

              const travelToAdd = Number(row._travelToAdd) || 0;
              const total = rate * adjustedUnits + travelToAdd;
              return (
                <span style={{ color: '#16a34a', fontWeight: 700 }}>
                  {formatCurrency(total)}
                </span>
              );
            }}
          />
        </DataTable>

        <WarningDialog
          header="אישור תשלום משכורת"
          isVisible={approveDialogVisible}
          onHide={() => setApproveDialogVisible(false)}
          addIcon={true}
          content="האם לבצע תשלום עבור כל השעות הממתינות לפי הסינון והמרצים שנבחרו?"
          notInformation={approveDialogSummary}
          notInformationVariant="blue"
          confirmLabel="אישור תשלום"
          confirmLoading={approveLoading}
          onConfirm={handleApprovePayment}
          cancelLabel="ביטול"
        />
      </div>
    </div>
  );
};
